import type { ContentType } from "@amzn/g4sservice-client";
import { TextArea, Button, Icon, Flex } from "@amzn/storm-ui";
import { longArrowUp, sparkle } from "@amzn/storm-ui-icons";
import { useMountEffect } from "@react-hookz/web";
import { isEmpty } from "lodash";
import type { ReactNode } from "react";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { isDeepEqual } from "remeda";
import styled from "styled-components";
import { useStore } from "zustand";

import useAutosizeTextArea from "../../../../hooks/misc/useAutosizeTextArea";
import { exploreWithAiStore } from "../../../../store/exploreWithAi";
import { discoveryFilterStore } from "../../../../store/filters";
import type { Item } from "../../../../types/common";
import { InsightsFromAIFilterField } from "../../../../types/filters/insightFromAI";
import { IHubContentProvider } from "../../../../types/providers";

import type { ExamplePrompt } from "./ExamplePrompts";
import { ExamplePrompts } from "./ExamplePrompts";

const WrapperWithBorder = styled(Flex)<{
    isEditing?: boolean;
    isLoading?: boolean;
}>`
    justify-content: space-between;
    align-items: center;
    padding: 5px;
    border-radius: 12px;
    background:
        linear-gradient(to right, white, white),
        linear-gradient(to right, #6c57d6, #c754b1);
    background-clip: padding-box, border-box;
    background-origin: padding-box, border-box;
    background-color: #fbf7ff;

    ${({ isEditing }) =>
        isEditing
            ? `
            border: 2px solid transparent;
        `
            : `
            border: 1px solid transparent;
    `}
`;

const PaddedDiv = styled.div`
    padding: 3px;
`;

const StyledTextArea = styled(TextArea)<{
    isEditing?: boolean;
    isLoading?: boolean;
}>`
    width: 100%;
    div {
        textarea {
            min-height: 32px;
            resize: none;
            border: none;
            outline: none;
            box-shadow: none;
            ${({ isEditing }) => !isEditing && `cursor: pointer;`}
            ${({ isLoading }) => isLoading && `cursor: not-allowed!important;`}
        }
        textarea: focus-within {
            outline: none;
        }
    }
`;

const SubmitButton = styled(Button)<{
    submitButtonText: string | undefined;
}>`
    ${({ submitButtonText }) => !submitButtonText && `height: 30px;`}
    color: white !important;
    :disabled {
        background-color: ${({ theme }) => theme.color.gray[500]};
    }
    ${({ submitButtonText, theme }) =>
        submitButtonText && submitButtonText.length > 0
            ? `
            right: ${theme.spacing.xsmall.default};
        `
            : `
            padding: ${theme.spacing.none.default} 9px;
    `}
`;

const HelpMessage = styled(Flex)`
    width: 70%;
    position: absolute;
    gap: ${({ theme }) => theme.spacing.xxxsmall.default};
    padding: 14px;
    justify-content: flex-start;
    flex-wrap: wrap;
    color: ${({ theme }) => theme.color.gray[800]};
`;

const AiIcon = styled(Icon)`
    color: ${({ theme }) => theme.color.purple[800]};
`;

interface PromptTextAreaProps {
    onSubmit: (
        prompt: string,
        isExamplePrompt: boolean,
        brandId?: string,
        browseNodeId?: string,
        contentTypes?: ContentType[],
    ) => void;
    isLoading?: boolean;
    placeholder?: string;
    examplePrompts?: ExamplePrompt[];
    initialValue?: string;
    helpMessage?: ReactNode;
    submitButtonText?: string;
}

export const PromptTextArea: React.FunctionComponent<PromptTextAreaProps> = ({
    onSubmit,
    isLoading,
    placeholder,
    examplePrompts,
    initialValue,
    helpMessage,
    submitButtonText,
}): JSX.Element => {
    const [isEditing, setIsEditing] = useState(true);
    const [prompt, setPrompt] = useState<string>("");
    const [isExamplePrompt, setIsExamplePrompt] = useState<boolean>(false);
    const [showHelpMessage, setShowHelpMessage] = useState(!!helpMessage);

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useAutosizeTextArea(textAreaRef.current, prompt, -6);

    const [storePrompt, storeBrandId, storeBrowseNodeId, storeContentTypes] =
        useStore(exploreWithAiStore, (state) => [
            state.prompt,
            state.brandId,
            state.browseNodeId,
            state.contentTypes,
        ]);

    const [fieldSelections] = useStore(discoveryFilterStore, (state) => [
        state.getFilters()[IHubContentProvider.INSIGHTS_FROM_AI]
            .fieldSelections,
    ]);

    const contentTypeSelections = fieldSelections[
        InsightsFromAIFilterField.CONTENT_TYPES
    ] as Item[];
    const brandSelections = fieldSelections[
        InsightsFromAIFilterField.BRAND
    ] as Item[];
    const browseNodeSelections = fieldSelections[
        InsightsFromAIFilterField.BROWSE_NODE
    ] as Item[];

    const contentTypes = contentTypeSelections.map(
        (selection) => selection.value as string,
    );
    const brandId = brandSelections[0]?.value as string;
    const browseNodeId = browseNodeSelections[0]?.value as string;

    const isSubmitDisabled = useMemo(() => {
        return (
            prompt.trim().length === 0 ||
            isLoading ||
            (prompt === storePrompt &&
                !!storeContentTypes &&
                isDeepEqual(contentTypes, storeContentTypes) &&
                brandId === storeBrandId &&
                browseNodeId === storeBrowseNodeId)
        );
    }, [
        prompt,
        storePrompt,
        brandId,
        browseNodeId,
        contentTypes,
        storeBrandId,
        storeBrowseNodeId,
        storeContentTypes,
        isLoading,
    ]);

    useMountEffect(() => {
        if (!isEmpty(storePrompt)) {
            setPrompt(storePrompt);
        }
        if (initialValue) {
            setPrompt(initialValue);
        }
    });

    useEffect(() => {
        if (isEditing) {
            textAreaRef.current?.focus();
        }
    }, [isEditing]);

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrompt(e.target.value);
        setShowHelpMessage(false);
        setIsExamplePrompt(false);
    };

    const handleExamplePromptSelect = (value: string) => {
        setPrompt(value);
        setIsEditing(true);
        setIsExamplePrompt(true);
    };

    const handleSubmit = () => {
        onSubmit(
            prompt,
            isExamplePrompt,
            brandId,
            browseNodeId,
            contentTypes as ContentType[],
        );
        setIsEditing(false);
    };

    return (
        <>
            {examplePrompts && (
                <ExamplePrompts
                    prompts={examplePrompts}
                    onClick={handleExamplePromptSelect}
                />
            )}
            <WrapperWithBorder isEditing={isEditing}>
                <PaddedDiv>
                    <AiIcon size="base" type={sparkle} />
                </PaddedDiv>
                <StyledTextArea
                    isEditing={isEditing}
                    isLoading={isLoading}
                    onBlur={() => setIsEditing(false)}
                    onFocus={() => setIsEditing(true)}
                    fullWidth={true}
                    readOnly={!isEditing}
                    id="prompt-textarea"
                    data-testid="prompt-textarea"
                    label=""
                    placeholder={placeholder}
                    value={prompt}
                    onChange={handleTextAreaChange}
                    inputRef={textAreaRef}
                />
                <PaddedDiv>
                    <SubmitButton
                        submitButtonText={submitButtonText}
                        data-testid="prompt-submit-button"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        variant="primary"
                    >
                        {submitButtonText ? (
                            <>
                                <Icon type={sparkle} /> {submitButtonText}
                            </>
                        ) : (
                            <Icon
                                type={longArrowUp}
                                size="large"
                                transform="rotate-90"
                            />
                        )}
                    </SubmitButton>
                </PaddedDiv>
            </WrapperWithBorder>
            {showHelpMessage && <HelpMessage>{helpMessage}</HelpMessage>}
        </>
    );
};
